import React from "react";
import { DataStudioPipelineStrip } from "../../molecules/DataStudioPipelineStrip";
import { DataStudioColumnDetailsModal } from "../DataStudioColumnDetailsModal";
import { DataStudioProfileGrid } from "./DataStudioProfileGrid";
import { DataStudioProfileHeader } from "./DataStudioProfileHeader";
import {
  ProfileCard,
  ProfileContent,
  ProfileLayout,
} from "./DataStudioProfileTable.styles";
import type { DataStudioProfileTableProps } from "./DataStudioProfileTable.types";
import { useDataStudioProfileTableInternalState } from "./useDataStudioProfileTableInternalState";

export const DataStudioProfileTable: React.FC<DataStudioProfileTableProps> = ({
  profile,
  onColumnTransform,
  onColumnMenu,
  onColumnImportanceChange,
  onPipelineStageSelect,
  onPipelineUndo,
  onPipelineRedo,
  isLoadingMoreRows = false,
  onLoadMoreRows,
  isRefreshingRowsForColumns = false,
  isLoadingOlderRows = false,
  onLoadOlderRows,
  hasOlderRows = false,
  rowsErrorMessage = null,
  hasMoreColumns = false,
  isLoadingMoreColumns = false,
  onLoadMoreColumns,
  isLoadingOlderColumns = false,
  onLoadOlderColumns,
  hasOlderColumns = false,
  columnsErrorMessage = null,
}) => {
  const initialImportantColumnIds = React.useMemo(
    () =>
      profile.columns
        .filter((column) => column.isImportant)
        .map((column) => column.id),
    [profile.columns],
  );
  const {
    visibleRows,
    visibleRowCount,
    hasMoreRows,
    handleViewportScroll,
    handleGridScroll,
    detailsColumnId,
    handleColumnMenu,
    importantColumnIds,
    closeColumnDetails,
    isAtColumnsStart,
    isAtColumnsEnd,
  } = useDataStudioProfileTableInternalState(
    profile.table.id,
    profile.rows,
    profile.rowPageInfo.hasMore,
    isLoadingMoreRows,
    onLoadMoreRows,
    onColumnMenu,
    initialImportantColumnIds,
    onColumnImportanceChange,
    hasOlderRows,
    isLoadingOlderRows,
    onLoadOlderRows,
    hasMoreColumns,
    isLoadingMoreColumns,
    onLoadMoreColumns,
    hasOlderColumns,
    isLoadingOlderColumns,
    onLoadOlderColumns,
  );
  return (
    <>
      <ProfileLayout>
        <ProfileContent>
          <ProfileCard>
            <DataStudioProfileHeader table={profile.table} />
            <DataStudioProfileGrid
              tableName={profile.table.name}
              columns={profile.columns}
              visibleRows={visibleRows}
              visibleRowCount={visibleRowCount}
              hasMoreRows={hasMoreRows}
              isLoadingMoreRows={isLoadingMoreRows}
              isRefreshingRowsForColumns={isRefreshingRowsForColumns}
              isLoadingOlderRows={isLoadingOlderRows}
              hasOlderRows={hasOlderRows}
              rowsErrorMessage={rowsErrorMessage}
              onViewportScroll={handleViewportScroll}
              onGridScroll={handleGridScroll}
              hasMoreColumns={hasMoreColumns}
              isLoadingMoreColumns={isLoadingMoreColumns}
              hasOlderColumns={hasOlderColumns}
              isLoadingOlderColumns={isLoadingOlderColumns}
              isAtColumnsStart={isAtColumnsStart}
              isAtColumnsEnd={isAtColumnsEnd}
              columnsErrorMessage={columnsErrorMessage}
              onColumnTransform={onColumnTransform}
              onColumnMenu={handleColumnMenu}
              importantColumnIds={importantColumnIds}
            />
          </ProfileCard>

          <DataStudioPipelineStrip
            pipeline={profile.pipeline}
            onStageSelect={onPipelineStageSelect}
            onUndo={onPipelineUndo}
            onRedo={onPipelineRedo}
          />
        </ProfileContent>
      </ProfileLayout>
      {detailsColumnId ? (
        <DataStudioColumnDetailsModal
          open
          connectionId={profile.table.source.id}
          assetId={profile.table.id}
          columnId={detailsColumnId}
          table={{
            name: profile.table.name,
            sourceName: profile.table.source.name,
            rowCount: profile.table.rowCount,
          }}
          onClose={closeColumnDetails}
        />
      ) : null}
    </>
  );
};

export default DataStudioProfileTable;
